import React, { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../utils/config";
import {
  FaBriefcase, FaSearch, FaMapMarkerAlt, FaClock, FaRupeeSign, FaEye, FaInfoCircle, FaHeart, FaRegHeart
} from "react-icons/fa";

const AllJobs = () => {
  const { allJobs, searchQuery: globalSearchQuery, savedJobs = [], setSavedJobs, toggleSaveJob, profile } = useOutletContext();
  const navigate = useNavigate();

  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [salaryFilter, setSalaryFilter] = useState("");
  const [showMatchedOnly, setShowMatchedOnly] = useState(true); // Default to true if profile exists

  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [salaries, setSalaries] = useState([]);

  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [isSalaryDropdownOpen, setIsSalaryDropdownOpen] = useState(false);
  const [salarySearchQuery, setSalarySearchQuery] = useState("");
  const [isDeptDropdownOpen, setIsDeptDropdownOpen] = useState(false);
  const [deptSearchQuery, setDeptSearchQuery] = useState("");

  const roleDropdownRef = React.useRef(null);
  const salaryDropdownRef = React.useRef(null);
  const deptDropdownRef = React.useRef(null);

  useEffect(() => {
    fetchFilterOptions();

    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setIsRoleDropdownOpen(false);
      }
      if (salaryDropdownRef.current && !salaryDropdownRef.current.contains(event.target)) {
        setIsSalaryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync showMatchedOnly with profile existence
  useEffect(() => {
    if (profile) {
      setShowMatchedOnly(true);
    } else {
      setShowMatchedOnly(false);
    }
  }, [profile]);

  const fetchFilterOptions = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/jobs/all`);
      if (res.data.success) {
        const jobData = res.data.data || res.data.jobPosts || [];

        // Extract unique departments
        const deptArr = Array.from(new Set(jobData.map(job => job.department).filter(Boolean))).sort();
        setDepartments(deptArr);

        // Extract unique roles
        const roleNames = Array.from(new Set(jobData.map(job => job.role).filter(Boolean))).sort();
        setRoles(roleNames);

        // Extract unique salaries
        const salArr = Array.from(new Set(jobData.map(job => job.salary).filter(Boolean))).sort();
        setSalaries(salArr);
      }
    } catch (err) {
      console.error("Failed to fetch filter options:", err);
    }
  };

  const filteredJobs = allJobs
    .filter(job => {
      // Profile Match logic
      let matchesProfile = true;
      if (profile && showMatchedOnly) {
        const candidateRoles = Array.isArray(profile.role) ? profile.role : (profile.role ? [profile.role] : []);
        const candidateDepts = Array.isArray(profile.department) ? profile.department : (profile.department ? [profile.department] : []);
        
        // Match if job role is in candidate's roles OR job department is in candidate's departments
        const roleMatch = candidateRoles.some(r => r.toLowerCase() === (job.role || "").toLowerCase());
        const deptMatch = candidateDepts.some(d => d.toLowerCase() === (job.department || "").toLowerCase());
        
        matchesProfile = roleMatch || deptMatch;
      }

      const combinedQuery = (globalSearchQuery || localSearchQuery).toLowerCase();
      const matchesSearch =
        job.role.toLowerCase().includes(combinedQuery) ||
        job.location.toLowerCase().includes(combinedQuery) ||
        (job.salary || "").toLowerCase().includes(combinedQuery);

      const matchesDepartment = departmentFilter ? job.department === departmentFilter : true;
      const matchesRole = roleFilter ? job.role === roleFilter : true;
      const matchesSalary = salaryFilter ? job.salary === salaryFilter : true;

      return matchesProfile && matchesSearch && matchesDepartment && matchesRole && matchesSalary;
    })
    .sort((a, b) => {
      const today = new Date().setHours(0, 0, 0, 0);
      const aIsExpired = a.expiryDate && new Date(a.expiryDate) < today;
      const bIsExpired = b.expiryDate && new Date(b.expiryDate) < today;

      if (aIsExpired && !bIsExpired) return 1;
      if (!aIsExpired && bIsExpired) return -1;

      const aIsSaved = savedJobs.some(sj => (sj._id || sj) === a._id);
      const bIsSaved = savedJobs.some(sj => (sj._id || sj) === b._id);
      if (aIsSaved && !bIsSaved) return -1;
      if (!aIsSaved && bIsSaved) return 1;

      return 0;
    });

  const resetFilters = () => {
    setLocalSearchQuery("");
    setRoleFilter("");
    setDepartmentFilter("");
    setSalaryFilter("");
    setRoleSearchQuery("");
    setDeptSearchQuery("");
    setSalarySearchQuery("");
    setShowMatchedOnly(!!profile);
  };

  return (
    <div className="w-full h-full">

      {/* ── Header & Filters ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-8">
        <div className="flex flex-wrap items-center gap-4">

          <div className="flex items-center gap-4 mr-auto">
            <div className="p-3 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl text-blue-600 shadow-sm border border-blue-100/50">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-blue-600 flex items-center justify-center text-white shadow-md">
                <FaBriefcase size={20} />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-blue-600">ALL JOBS</h1>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Explore Opportunities</div>
            </div>
          </div>

          {/* Search */}
          <div className="relative w-64">
            <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search by role, location or salary..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs font-bold border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none uppercase tracking-widest"
            />
          </div>

          {/* Role Filter */}
          <div className="relative" ref={roleDropdownRef}>
            <button
              onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
              className={`h-9 px-4 text-[10px] font-bold rounded-xl transition flex items-center gap-2 uppercase tracking-widest border ${roleFilter
                ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
            >
              <FaBriefcase className={roleFilter ? 'text-white' : 'text-blue-500'} size={12} />
              Role {roleFilter && `: ${roleFilter}`}
            </button>

            {isRoleDropdownOpen && (
              <div className="absolute z-50 mt-2 w-64 bg-white border border-gray-100 rounded-2xl shadow-xl p-2 animate-in fade-in zoom-in-95 duration-200 origin-top">
                <div className="p-2 border-b border-gray-50 mb-1">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
                    <input
                      type="text"
                      className="w-full py-2 pl-8 pr-3 text-[10px] bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold uppercase tracking-wider"
                      placeholder="Search roles..."
                      value={roleSearchQuery}
                      onChange={(e) => setRoleSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto no-scrollbar">
                  <div
                    onClick={() => { setRoleFilter(''); setIsRoleDropdownOpen(false); setRoleSearchQuery(''); }}
                    className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl cursor-pointer mb-1 transition-all ${!roleFilter ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
                  >
                    All Roles
                  </div>
                  {roles
                    .filter(r => r.toLowerCase().includes(roleSearchQuery.toLowerCase()))
                    .map((r, i) => (
                      <div
                        key={i}
                        onClick={() => { setRoleFilter(r); setIsRoleDropdownOpen(false); setRoleSearchQuery(''); }}
                        className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl cursor-pointer mb-1 transition-all ${roleFilter === r ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
                      >
                        {r}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

            {/* Department Filter */}
            <div className="relative" ref={deptDropdownRef}>
              <button
                onClick={() => setIsDeptDropdownOpen(!isDeptDropdownOpen)}
                className={`h-9 px-4 text-[10px] font-bold rounded-xl transition flex items-center gap-2 uppercase tracking-widest border ${departmentFilter
                  ? 'bg-purple-600 text-white border-purple-600 shadow-md'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <FaBriefcase className={departmentFilter ? 'text-white' : 'text-purple-500'} size={12} />
                Dept {departmentFilter && `: ${departmentFilter}`}
              </button>

              {isDeptDropdownOpen && (
                <div className="absolute z-50 mt-2 w-64 bg-white border border-gray-100 rounded-2xl shadow-xl p-2 animate-in fade-in zoom-in-95 duration-200 origin-top">
                  <div className="p-2 border-b border-gray-50 mb-1">
                    <div className="relative">
                      <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
                      <input
                        type="text"
                        className="w-full py-2 pl-8 pr-3 text-[10px] bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold uppercase tracking-wider"
                        placeholder="Search departments..."
                        value={deptSearchQuery}
                        onChange={(e) => setDeptSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto no-scrollbar">
                    <div
                      onClick={() => { setDepartmentFilter(''); setIsDeptDropdownOpen(false); setDeptSearchQuery(''); }}
                      className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl cursor-pointer mb-1 transition-all ${!departmentFilter ? 'bg-purple-50 text-purple-600' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                      All Departments
                    </div>
                    {departments
                      .filter(d => d.toLowerCase().includes(deptSearchQuery.toLowerCase()))
                      .map((d, i) => (
                        <div
                          key={i}
                          onClick={() => { setDepartmentFilter(d); setIsDeptDropdownOpen(false); setDeptSearchQuery(''); }}
                          className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl cursor-pointer mb-1 transition-all ${departmentFilter === d ? 'bg-purple-50 text-purple-600' : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                          {d}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Salary Filter */}
            <div className="relative" ref={salaryDropdownRef}>
              <button
                onClick={() => setIsSalaryDropdownOpen(!isSalaryDropdownOpen)}
              className={`h-9 px-4 text-[10px] font-bold rounded-xl transition flex items-center gap-2 uppercase tracking-widest border ${salaryFilter
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-md'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
            >
              <FaRupeeSign className={salaryFilter ? 'text-white' : 'text-emerald-500'} size={12} />
              Salary {salaryFilter && `: ${salaryFilter}`}
            </button>

            {isSalaryDropdownOpen && (
              <div className="absolute z-50 mt-2 w-64 bg-white border border-gray-100 rounded-2xl shadow-xl p-2 animate-in fade-in zoom-in-95 duration-200 origin-top">
                <div className="p-2 border-b border-gray-50 mb-1">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px]" />
                    <input
                      type="text"
                      className="w-full py-2 pl-8 pr-3 text-[10px] bg-gray-50 border border-transparent rounded-xl focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold uppercase tracking-wider"
                      placeholder="Search salaries..."
                      value={salarySearchQuery}
                      onChange={(e) => setSalarySearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto no-scrollbar">
                  <div
                    onClick={() => { setSalaryFilter(''); setIsSalaryDropdownOpen(false); setSalarySearchQuery(''); }}
                    className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl cursor-pointer mb-1 transition-all ${!salaryFilter ? 'bg-emerald-50 text-emerald-600' : 'text-gray-500 hover:bg-gray-50'}`}
                  >
                    All Salaries
                  </div>
                  {salaries
                    .filter(s => s.toLowerCase().includes(salarySearchQuery.toLowerCase()))
                    .map((sal, i) => (
                      <div
                        key={i}
                        onClick={() => { setSalaryFilter(sal); setIsSalaryDropdownOpen(false); setSalarySearchQuery(''); }}
                        className={`px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-xl cursor-pointer mb-1 transition-all ${salaryFilter === sal ? 'bg-emerald-50 text-emerald-600' : 'text-gray-500 hover:bg-gray-50'}`}
                      >
                        {sal}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Job View Toggles */}
          {profile && (
            <div className="flex bg-gray-100 p-1 rounded-xl">
              <button
                onClick={() => setShowMatchedOnly(true)}
                className={`h-8 px-4 text-[10px] font-bold rounded-lg transition-all flex items-center gap-2 uppercase tracking-widest ${showMatchedOnly
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                {showMatchedOnly && <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></div>}
                Matched Jobs
              </button>
              <button
                onClick={() => setShowMatchedOnly(false)}
                className={`h-8 px-4 text-[10px] font-bold rounded-lg transition-all flex items-center gap-2 uppercase tracking-widest ${!showMatchedOnly
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                {!showMatchedOnly && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>}
                All Jobs
              </button>
            </div>
          )}

          {/* Reset */}
          {(localSearchQuery || roleFilter || salaryFilter || (profile && !showMatchedOnly)) && (
            <button
              onClick={resetFilters}
              className="h-9 px-4 text-[10px] font-bold text-gray-600 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition uppercase tracking-widest shadow-sm"
            >
              Clear Filters
            </button>
          )}

        </div>
      </div>

      {/* ── Table Area ── */}
      <div className="p-0 bg-white border shadow-lg rounded-xl overflow-hidden">
        {filteredJobs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="text-xs text-left text-white bg-gradient-to-r from-green-500 to-blue-600">
                <tr>
                  <th className="py-3 px-4 text-center font-bold">Company</th>
                  <th className="py-3 px-4 text-center font-bold">Job Position</th>
                  <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Location</th>
                  <th className="py-3 px-4 text-center font-bold">Experience</th>
                  <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Salary</th>
                  <th className="py-3 px-4 text-center font-bold hidden md:table-cell">Vacancies</th>
                  <th className="py-3 px-4 text-center font-bold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredJobs.map((job) => (
                  <tr key={job._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2 text-center">
                      <span className="text-[10px] font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded uppercase tracking-wider">
                        {job.companyName || "Timely Health"}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="text-xs font-bold text-gray-800">{job.role}</div>
                      {job.jobType && (
                        <div className="text-[9px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">{job.jobType}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center hidden md:table-cell">
                      <div className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                        <FaMapMarkerAlt className="text-blue-400" size={10} />
                        {job.location}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center align-middle">
                      <div className="inline-flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider bg-gray-50 px-2.5 py-1 rounded-md border border-gray-100 shadow-sm">
                        <FaClock className="text-amber-500" size={11} />
                        {job.experience || "Fresher"}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center align-middle hidden md:table-cell">
                      <div className="inline-flex items-center gap-1.5 text-[10px] font-bold text-gray-700 uppercase tracking-wider bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-100 shadow-sm">
                        <FaRupeeSign className="text-emerald-600" size={11} />
                        {job.salary || "N/A"}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center hidden md:table-cell">
                      <span className="text-[10px] font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
                        {job.vacancies || 1}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                        <button
                          onClick={() => toggleSaveJob(job._id)}
                          className={`px-3 py-1 text-[9px] font-bold uppercase tracking-widest rounded-lg border transition-all ${savedJobs.some(sj => (sj._id || sj) === job._id)
                              ? 'bg-red-50 text-green-600 border-red-100 hover:bg-red-100'
                              : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                            }`}
                        >
                          <div className="flex items-center gap-1.5">
                            {savedJobs.some(sj => (sj._id || sj) === job._id) ? <FaHeart /> : <FaRegHeart />}
                            {savedJobs.some(sj => (sj._id || sj) === job._id) ? "Saved" : "Save Job"}
                          </div>
                        </button>
                        {job.expiryDate && new Date(job.expiryDate) < new Date(new Date().setHours(0, 0, 0, 0)) ? (
                          <div className="px-3 py-1 bg-red-50 text-red-600 text-[9px] font-bold uppercase tracking-widest rounded-lg border border-red-100 flex items-center gap-1.5 shadow-sm opacity-80 cursor-not-allowed">
                            This job is Expired
                          </div>
                        ) : (
                          <button
                            onClick={() => navigate(`/jobs/${job._id}`)}
                            className="px-3 py-1 bg-blue-600 text-white text-[9px] font-bold uppercase tracking-widest rounded-lg hover:bg-blue-700 transition-all flex items-center gap-1.5 shadow-sm"
                          >
                            <FaEye size={10} /> View and Apply
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3 text-gray-300">
              <FaBriefcase size={20} />
            </div>
            <h3 className="text-sm font-bold text-gray-700 mb-1">No Jobs Found</h3>
            <p className="text-[10px] text-gray-400 font-medium tracking-wider">We couldn't find any jobs matching your current filters.</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest pt-6 pb-2">
        <FaInfoCircle className="text-blue-400" />
        Timely Health Recruitment Tracker • {new Date().getFullYear()}
      </div>
    </div>
  );
};

export default AllJobs;